import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../../types';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';
import { useInView } from '../../hooks/useInView';
import { getProductPriceInfo } from '../../utils/pricing';

interface RelatedProductsProps {
  currentProduct: Product;
}

const formatCategoryName = (slug: string): string =>
  slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const RelatedProducts: React.FC<RelatedProductsProps> = ({ currentProduct }) => {
  const { products } = useSupabaseData();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.08 });

  const { related, sharedTag } = useMemo(() => {
    // Prefer matching the product's subcategory slug (second element in
    // categories array, since the first is usually the top-level category).
    const subcat = currentProduct.categories?.[1];
    const topcat = currentProduct.categories?.[0];

    let matches: Product[] = [];
    let tag = '';

    if (subcat) {
      matches = products.filter(
        p => p.id !== currentProduct.id && p.is_active && (p.categories || []).includes(subcat)
      );
      tag = subcat;
    }

    // Fallback to top-level category
    if (matches.length < 4 && topcat) {
      const extra = products.filter(
        p =>
          p.id !== currentProduct.id &&
          p.is_active &&
          (p.categories || []).includes(topcat) &&
          !matches.some(m => m.id === p.id)
      );
      matches = [...matches, ...extra];
      if (!tag) tag = topcat;
    }

    // Sort by sort_order ascending
    matches.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

    return { related: matches.slice(0, 8), sharedTag: tag };
  }, [products, currentProduct]);

  if (related.length === 0) return null;

  return (
    <section
      ref={ref}
      className={`max-w-4xl mx-auto px-4 sm:px-6 mt-24 transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Ornate divider */}
      <div className="flex items-center justify-center mb-10" aria-hidden="true">
        <div className="h-px bg-warm-200 flex-1 max-w-[140px]" />
        <div className="mx-5 flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-gold-400/70" />
          <div className="w-1.5 h-1.5 rounded-full bg-gold-500" />
          <div className="w-1 h-1 rounded-full bg-gold-400/70" />
        </div>
        <div className="h-px bg-warm-200 flex-1 max-w-[140px]" />
      </div>

      {/* Header */}
      <div className="text-center mb-10">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.35em] text-gold-700 font-accent italic mb-3">
          — Kindred Remedies —
        </div>
        <h2 className="font-display text-[28px] sm:text-[34px] md:text-[40px] text-forest-900 leading-[1.05] tracking-tight">
          Related Products
        </h2>
        {sharedTag && (
          <p className="mt-2 text-[13px] text-warm-500 font-accent italic">
            Others in <span className="text-gold-700">{formatCategoryName(sharedTag)}</span>
          </p>
        )}
      </div>

      {/* Horizontal scroll rail */}
      <div className="-mx-4 sm:-mx-6 overflow-x-auto scrollbar-hide">
        <div className="flex gap-5 px-4 sm:px-6 snap-x snap-mandatory">
          {related.map((p) => {
            const info = getProductPriceInfo(p);
            const price = info.finalPrice;
            const origPrice = info.originalPrice;
            const discount = info.discount;
            return (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="group flex-shrink-0 w-[220px] sm:w-[240px] snap-start"
              >
                <div className="relative bg-cream-50 border border-warm-200 rounded-sm overflow-hidden transition-all duration-500 group-hover:border-gold-400/70 group-hover:shadow-[0_4px_22px_-8px_rgba(30,58,45,0.15)]">
                  {/* Image frame */}
                  <div className="relative aspect-square bg-white flex items-center justify-center overflow-hidden">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.name || ''}
                        loading="lazy"
                        className="w-full h-full object-contain p-5 transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="text-warm-300 text-4xl font-display italic">℞</div>
                    )}
                    {/* Thin gold inner border on hover */}
                    <div className="absolute inset-0 border border-transparent group-hover:border-gold-300/40 transition-colors duration-500" aria-hidden="true" />
                  </div>

                  {/* Info */}
                  <div className="px-4 py-4 border-t border-warm-200">
                    <h3 className="font-display text-[15px] text-forest-900 leading-tight line-clamp-2 mb-2">
                      {p.name}
                    </h3>
                    <div className="flex items-baseline justify-between gap-2">
                      <div>
                        {origPrice > price && (
                          <div className="text-[10px] text-warm-400 line-through leading-none mb-0.5">
                            ${origPrice.toFixed(2)}
                          </div>
                        )}
                        <div className="font-display text-[18px] text-forest-900 leading-none">
                          ${price.toFixed(2)}
                        </div>
                      </div>
                      {discount > 0 && (
                        <span className="text-[9px] uppercase tracking-[0.15em] text-gold-700 font-accent italic">
                          −{discount}%
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Corner flourish */}
                  <div className="absolute top-2 right-2 text-gold-400/40 text-[14px] font-display italic leading-none select-none pointer-events-none" aria-hidden="true">
                    ❦
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Decorative footer rule */}
      <div className="flex items-center justify-center mt-12" aria-hidden="true">
        <div className="h-px bg-warm-200 w-20" />
        <div className="mx-4 text-gold-500 text-sm font-display italic">✦</div>
        <div className="h-px bg-warm-200 w-20" />
      </div>
    </section>
  );
};

export default RelatedProducts;
